import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Box, BoxProps, IconButton, Stack, StackProps, Typography, TypographyProps } from '@mui/material';
import { isString } from 'lodash';
import { ForwardedRef, ReactNode, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { surfaceSx } from '../../styles/tokens';
import { FlexBlock } from '../common';

export const PageContainer = forwardRef<HTMLDivElement, any>(({ sx = {}, ...props }: StackProps, ref) => (
  <Stack
    ref={ref}
    padding={{ xs: 2, md: 4 }}
    paddingTop={{ xs: 1, md: 2 }}
    spacing={3}
    {...props}
    sx={{ height: '100%', maxWidth: '100%', marginX: 'auto', ...sx }}
  />
));

export const PageTitle = (props: TypographyProps) => <Typography variant="h1" typography="h5" {...props} />;

export type HeaderBreakpoint = 'md' | 'lg' | 'xl';

export interface PageHeaderProps extends Omit<BoxProps, 'title'> {
  backPath?: string;
  title: React.ReactNode | string;
  /** Width from which the title and `extra` sit side by side; below it they stack. */
  headerBreakpoint?: HeaderBreakpoint;
}

export const PageHeader = ({ backPath, title, children, headerBreakpoint = 'md', ...props }: PageHeaderProps) => {
  const navigate = useNavigate();

  return (
    <Box
      display="flex"
      flexDirection={{ xs: 'column', [headerBreakpoint]: 'row' }}
      gap={1}
      alignItems={{ [headerBreakpoint]: 'center' }}
      {...props}
    >
      <Stack direction="row" alignItems="center">
        {backPath && (
          <IconButton size="small" onClick={() => navigate(-1)}>
            <ArrowBackIosNewIcon />
          </IconButton>
        )}
        {isString(title) ? <PageTitle>{title}</PageTitle> : title}
      </Stack>
      <FlexBlock />
      {children}
    </Box>
  );
};

export const ScrollableContentBlock = ({ sx = {}, ...props }: BoxProps) => (
  <Box sx={{ height: 1, width: 1, overflow: 'auto', ...sx }} {...props} />
);

export const PageContentBlock = ({ sx = {}, ...props }: BoxProps) => (
  <ScrollableContentBlock
    sx={{
      padding: { xs: 1, md: 3 },
      ...surfaceSx,
      ...sx
    }}
    {...props}
  />
);

export interface PageLayoutProps extends Omit<StackProps, 'title'> {
  title?: ReactNode;
  extra?: ReactNode;
  extraFooter?: ReactNode;
  withContentBlock?: boolean;
  backPath?: string;
  headerBreakpoint?: HeaderBreakpoint;
}

export const PageLayout = forwardRef(
  (
    { title, children, withContentBlock, backPath, extra, extraFooter, headerBreakpoint, ...props }: PageLayoutProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => (
    <PageContainer {...props} ref={ref}>
      <PageHeader title={title} backPath={backPath} headerBreakpoint={headerBreakpoint}>
        {extra}
      </PageHeader>
      <FlexBlock>{withContentBlock ? <PageContentBlock>{children}</PageContentBlock> : children}</FlexBlock>
      {extraFooter}
    </PageContainer>
  )
);
